const sameId = (left, right) =>
  left != null && right != null && String(left) === String(right);

const timestamp = (value) => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const withMessageSummary = (conversation, message) => {
  if (!conversation || !sameId(conversation.id, message.conversation_id)) {
    return conversation;
  }
  return {
    ...conversation,
    messages_count: (conversation.messages_count || 0) + 1,
    last_message_at:
      timestamp(message.created_at) > timestamp(conversation.last_message_at)
        ? message.created_at
        : conversation.last_message_at,
  };
};

/**
 * Apply one message to every existing projection in the shared query cache.
 * Both list and detail socket hooks call this synchronous operation: identity
 * is checked before any counter or page is changed, including HTTP-seeded IDs.
 * No additional endpoint, storage, event subscription or auth scope is created.
 */
export const applyConversationMessageToCache = (queryClient, message) => {
  if (message?.id == null || message?.conversation_id == null) return false;

  const queries = queryClient
    .getQueriesData({ queryKey: ["conversationMessages"] })
    .filter(([key]) => sameId(key[1], message.conversation_id));
  if (
    queries.some(([, data]) =>
      data?.messages?.some((item) => sameId(item.id, message.id)),
    )
  ) {
    return false;
  }

  const targets = queries.length
    ? queries
    : [[["conversationMessages", message.conversation_id], undefined]];
  for (const [key, oldData] of targets) {
    const oldMessages = oldData?.messages || [];
    const descending = key[2]?.orderBy
      ? key[2].orderBy.startsWith("-")
      : oldMessages.length > 1 &&
        timestamp(oldMessages[0].created_at) >
          timestamp(oldMessages.at(-1).created_at);
    const messages = descending
      ? [message, ...oldMessages]
      : [...oldMessages, message];
    messages.sort((a, b) =>
      descending
        ? timestamp(b.created_at) - timestamp(a.created_at)
        : timestamp(a.created_at) - timestamp(b.created_at),
    );
    queryClient.setQueryData(key, {
      ...oldData,
      messages,
      pagination: {
        ...(oldData?.pagination || { page: 1, page_size: 50 }),
        total: (oldData?.pagination?.total ?? oldMessages.length) + 1,
      },
    });
  }

  queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
    if (!oldData?.conversations) return oldData;
    const conversations = oldData.conversations.map((item) =>
      withMessageSummary(item, message),
    );
    conversations.sort(
      (a, b) =>
        timestamp(b.last_message_at || b.created_at) -
        timestamp(a.last_message_at || a.created_at),
    );
    return { ...oldData, conversations };
  });

  // Detail queries include their projection params after the ID.
  queryClient.setQueriesData(
    { queryKey: ["conversation"] },
    (oldConversation) => withMessageSummary(oldConversation, message),
  );
  return true;
};
